import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set auth token in axios headers
  if (token) {
    axios.defaults.headers.common['x-auth-token'] = token;
  } else {
    delete axios.defaults.headers.common['x-auth-token'];
  }

  // Register user
  const register = async (userData) => {
    try {
      const res = await axios.post('/api/users/register', userData);

      localStorage.setItem('token', res.data.token);
      axios.defaults.headers.common['x-auth-token'] = res.data.token;
      setToken(res.data.token);

      await loadUser();
      return res.data;
    } catch (err) {
      console.error('Register error:', err.response.data);
      throw err;
    }
  };

  // Login user
  const login = async (userData) => {
    try {
      const res = await axios.post('/api/users/login', userData);

      // Set the header BEFORE calling loadUser. setToken is async and the
      // synchronous header-setting block at the top of the provider only
      // runs on the next render, so without this the immediately-following
      // /api/users/me call goes out with the stale (expired) token and
      // fails, forcing the user to log in a second time.
      localStorage.setItem('token', res.data.token);
      axios.defaults.headers.common['x-auth-token'] = res.data.token;
      setToken(res.data.token);

      await loadUser();
      return res.data;
    } catch (err) {
      console.error('Login error:', err.response.data);
      throw err;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['x-auth-token'];
    setToken(null);
    setCurrentUser(null);
  };

  // Load user
  const loadUser = async () => {
    try {
      const res = await axios.get('/api/users/me');
      setCurrentUser(res.data);
    } catch (err) {
      console.error('Load user error:', err);
      setCurrentUser(null);
    }
    setLoading(false);
  };

  // Check if token is expired
  const isTokenExpired = () => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch (err) {
      return true;
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const res = await axios.put('/api/users/me', userData);
      setCurrentUser(res.data);
      return res.data;
    } catch (err) {
      console.error('Update profile error:', err.response.data);
      throw err;
    }
  };

  useEffect(() => {
    // Check if token exists
    if (token && !isTokenExpired()) {
      axios.defaults.headers.common['x-auth-token'] = token;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        token,
        currentUser,
        setCurrentUser,
        loading,
        register,
        login,
        logout,
        loadUser,
        updateProfile,
        isAuthenticated: !!currentUser,
        isAdmin: currentUser?.role === 'admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;